function esc(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function nl2br(value) {
  return esc(value).replace(/\n/g, '<br/>');
}

function fieldRows(rows = []) {
  return rows.map((row) => `
    <tr>
      <td class="label">${esc(row.label || '')}</td>
      <td class="sep">:</td>
      <td class="value">${nl2br(row.value || '—')}</td>
    </tr>
  `).join('');
}

export function openPrintWindow({ title, bodyHtml }) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) {
    alert("Le navigateur a bloqué l'ouverture de la fenêtre d'impression.");
    return;
  }

  win.document.write(`
    <!DOCTYPE html>
    <html lang="fr">
      <head>
        <meta charset="utf-8" />
        <title>${esc(title)}</title>
        <style>
          :root {
            --text: #111;
            --muted: #555;
          }

          html, body {
            margin: 0;
            padding: 0;
            color: var(--text);
            background: #fff;
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            line-height: 1.35;
          }

          body {
            padding: 10px;
          }

          .ticket {
            width: 100%;
            max-width: 80mm;
            margin: 0 auto;
          }

          .header {
            text-align: center;
            margin-bottom: 8px;
          }

          .biz-name {
            font-size: 16px;
            font-weight: 700;
            text-transform: uppercase;
            margin-bottom: 4px;
          }

          .meta-line {
            font-size: 11px;
            color: var(--muted);
            margin-bottom: 2px;
            word-break: break-word;
          }

          .divider {
            border-top: 1px dashed #000;
            margin: 8px 0;
          }

          .receipt-title {
            text-align: center;
            font-size: 14px;
            font-weight: 700;
            margin: 6px 0 8px;
            text-transform: uppercase;
          }

          .section-title {
            font-size: 12px;
            font-weight: 700;
            margin: 8px 0 4px;
            text-transform: uppercase;
          }

          table.fields {
            width: 100%;
            border-collapse: collapse;
          }

          table.fields td {
            vertical-align: top;
            padding: 2px 0;
            word-break: break-word;
          }

          table.fields td.label {
            width: 38%;
            font-weight: 700;
          }

          table.fields td.sep {
            width: 4%;
            text-align: center;
          }

          table.fields td.value {
            width: 58%;
            text-align: left;
          }

          .signatures {
            margin-top: 18px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
          }

          .sign-box {
            text-align: center;
            font-size: 11px;
          }

          .sign-line {
            border-top: 1px solid #000;
            margin: 30px 0 4px;
          }

          .footer {
            margin-top: 10px;
            text-align: center;
            font-size: 11px;
            color: var(--muted);
          }

          .small {
            font-size: 11px;
          }

          @media screen and (min-width: 900px) {
            .ticket.ticket-wide {
              max-width: 148mm;
              font-size: 13px;
            }

            .ticket.ticket-wide .biz-name {
              font-size: 18px;
            }

            .ticket.ticket-wide .receipt-title {
              font-size: 16px;
            }

            .ticket.ticket-wide table.fields td.label {
              width: 30%;
            }

            .ticket.ticket-wide table.fields td.value {
              width: 66%;
            }
          }

          @page {
            size: auto;
            margin: 4mm;
          }

          @media print {
            body {
              padding: 0;
            }

            .ticket {
              max-width: none;
              width: 80mm;
            }

            .ticket.ticket-wide {
              width: 80mm;
            }
          }
        </style>
      </head>
      <body>
        ${bodyHtml}
      </body>
    </html>
  `);

  win.document.close();
  win.focus();

  setTimeout(() => {
    win.print();
  }, 350);
}

export function buildSettingsHeader(settings = {}) {
  return `
    <div class="header">
      <div class="biz-name">${esc(settings.businessName || 'Crystal Multi Services')}</div>
      ${settings.addressLine ? `<div class="meta-line">${esc(settings.addressLine)}</div>` : ''}
      ${settings.phoneLine ? `<div class="meta-line">Tél: ${esc(settings.phoneLine)}</div>` : ''}
      ${settings.cityLine ? `<div class="meta-line">${esc(settings.cityLine)}</div>` : ''}
      ${settings.headerText ? `<div class="meta-line">${nl2br(settings.headerText)}</div>` : ''}
    </div>
  `;
}

export function buildSettingsFooter(settings = {}) {
  return `
    <div class="footer">
      ${settings.receiptNote ? `<div class="small">${nl2br(settings.receiptNote)}</div>` : ''}
      ${settings.footerText ? `<div class="small" style="margin-top:4px;">${nl2br(settings.footerText)}</div>` : ''}
    </div>
  `;
}

export function buildViewPdfHtml({
  title,
  settings,
  sections = [],
}) {
  return `
    <div class="ticket ticket-wide">
      ${buildSettingsHeader(settings)}
      <div class="divider"></div>
      <div class="receipt-title">${esc(title)}</div>

      ${sections.map((section) => `
        <div class="section-title">${esc(section.title || '')}</div>
        <table class="fields">
          <tbody>
            ${fieldRows(section.rows || [])}
          </tbody>
        </table>
        <div class="divider"></div>
      `).join('')}

      ${buildSettingsFooter(settings)}
    </div>
  `;
}

export function buildReceiptPdfHtml({
  title,
  settings,
  rows = [],
  signatures = {},
}) {
  const clientSigner = signatures.client || 'Signature client';
  const cashierSigner = signatures.cashier || 'Signature utilisateur';

  return `
    <div class="ticket">
      ${buildSettingsHeader(settings)}
      <div class="divider"></div>
      <div class="receipt-title">${esc(title)}</div>

      <table class="fields">
        <tbody>
          ${fieldRows(rows)}
        </tbody>
      </table>

      <div class="signatures">
        <div class="sign-box">
          <div class="sign-line"></div>
          <div>${esc(clientSigner)}</div>
        </div>
        <div class="sign-box">
          <div class="sign-line"></div>
          <div>${esc(cashierSigner)}</div>
        </div>
      </div>

      ${buildSettingsFooter(settings)}
    </div>
  `;
}